import { apiClient } from './api-client';

/**
 * Group 4 — the per-EquipmentModel As-Found parameter configuration, plus the
 * tenant-wide DEFAULT template that seeds it.
 *
 * The fixed "Others" row never appears in any of these payloads. It is a system
 * constant the backend appends when an inspection is created; the pages render
 * it as a non-editable row, and sending it would be rejected.
 *
 * The default template is copied into a model BY VALUE by the editor — there is
 * no server-side "seed" call, and opening the editor writes nothing. That is why
 * a later template edit can never reach a model that was already configured.
 */
export interface InspectionParameter {
  id: string;
  equipmentModelId: string;
  label: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** One row of the tenant-wide default template. No model id, by design. */
export interface DefaultInspectionParameter {
  id: string;
  label: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** One row of the "Configured Equipment Models" overview. */
export interface InspectionParameterOverviewItem {
  equipmentModelId: string;
  name: string | null;
  modelNumber: string | null;
  parameterCount: number;
  /** False when the model has no configured parameters (D2-A). */
  isConfigured: boolean;
}

export const inspectionParameterService = {
  /** Configured parameters for a model, ascending. */
  async list(equipmentModelId: string): Promise<InspectionParameter[]> {
    const r = await apiClient.get(`/equipment-models/${equipmentModelId}/inspection-parameters`);
    // The response interceptor already unwrapped the envelope.
    return Array.isArray(r.data) ? (r.data as InspectionParameter[]) : [];
  },

  /**
   * Replaces the whole ordered configuration — the single "Save Configuration"
   * action. Array order IS the order; the server assigns sortOrder from it.
   */
  async save(equipmentModelId: string, labels: string[]): Promise<InspectionParameter[]> {
    const r = await apiClient.put(`/equipment-models/${equipmentModelId}/inspection-parameters`, {
      parameters: labels.map((label) => ({ label })),
    });
    return Array.isArray(r.data) ? (r.data as InspectionParameter[]) : [];
  },

  /** Every model in the tenant with its configured-parameter count. */
  async overview(): Promise<InspectionParameterOverviewItem[]> {
    const r = await apiClient.get('/equipment-models/inspection-parameters/overview');
    return Array.isArray(r.data) ? (r.data as InspectionParameterOverviewItem[]) : [];
  },
};

export const inspectionParameterDefaultsService = {
  /** The tenant-wide default template, ascending. Empty until an admin sets one. */
  async list(): Promise<DefaultInspectionParameter[]> {
    const r = await apiClient.get('/inspection-parameter-defaults');
    return Array.isArray(r.data) ? (r.data as DefaultInspectionParameter[]) : [];
  },

  /**
   * Replaces the whole ordered template.
   *
   * Affects only models configured from now on. It does not modify any existing
   * model configuration and never touches an inspection.
   */
  async save(labels: string[]): Promise<DefaultInspectionParameter[]> {
    const r = await apiClient.put('/inspection-parameter-defaults', {
      parameters: labels.map((label) => ({ label })),
    });
    return Array.isArray(r.data) ? (r.data as DefaultInspectionParameter[]) : [];
  },
};
